import React from 'react';
import { VariableSizeList } from 'react-window';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import styles from './InfiniteChannelList.module.css';
import Channel from '../Channel/Channel';
import { useRootStore } from '../../../../mobx/bridge';
import {
  datesHaveDifference,
  formatLatestMessageTimestamp,
  isToday,
  wasYesterday,
} from '../../../../helpers/dateHelpers';

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const VariableSizeListWrapper = observer(function VariableSizeListWrapper(props) {
  const { onItemsRendered, itemRef, listHeight } = props;
  const { commonStore, channelStore, messageStore } = useRootStore();
  const ref = React.useRef();
  const { t } = useTranslation();

  const channelsToDisplay = channelStore.getChannels;

  React.useEffect(() => {
    if (ref?.current) {
      ref.current.scrollTo(0);
    }
  }, [commonStore.isSettingsMenuOpen]);

  React.useEffect(() => {
    if (ref?.current) {
      ref.current.resetAfterIndex(0);
    }
  }, [channelsToDisplay]);

  const checkForDateSeparator = (index) => {
    let dateSeparator = null;

    const currentLastMessage = messageStore.computedGetLastMessageForChannel(
      channelsToDisplay[index]
    );

    if (currentLastMessage) {
      const currentLastMessageDate = new Date(currentLastMessage.Timestamp);

      const previousLastMessage = channelsToDisplay[index - 1]
        ? messageStore.computedGetLastMessageForChannel(channelsToDisplay[index - 1])
        : null;

      const previousLastMessageDate = previousLastMessage
        ? new Date(previousLastMessage.Timestamp)
        : null;

      if (
        previousLastMessageDate &&
        currentLastMessageDate &&
        isToday(previousLastMessageDate) &&
        wasYesterday(currentLastMessageDate)
      ) {
        dateSeparator = t('MessageList.Yesterday');
      } else if (
        previousLastMessageDate &&
        datesHaveDifference(currentLastMessageDate, previousLastMessageDate)
      ) {
        dateSeparator = formatLatestMessageTimestamp(currentLastMessageDate);
      }
    }

    return dateSeparator;
  };

  const Item = ({ index, style }) => {
    if (!channelsToDisplay[index]) {
      return <div className={styles.loadingText}>{t('Common.LoadingWithThreeDots')}</div>;
    }

    const dateSeparator = checkForDateSeparator(index);

    return (
      <div style={style}>
        {dateSeparator ? <div className={styles.dateSeparator}>{dateSeparator}</div> : null}

        <Channel key={channelsToDisplay[index]._id} channel={channelsToDisplay[index]} />
      </div>
    );
  };

  const setItemSize = (index) => (checkForDateSeparator(index) ? 96 : 64);

  return (
    <VariableSizeList
      itemCount={channelsToDisplay.length}
      onItemsRendered={onItemsRendered}
      ref={(list) => {
        itemRef(list);
        ref.current = list;
      }}
      height={listHeight}
      itemSize={setItemSize}
    >
      {Item}
    </VariableSizeList>
  );
});
VariableSizeListWrapper.whyDidYouRender = true;
VariableSizeListWrapper.displayName = 'VariableSizeListWrapper';

export default VariableSizeListWrapper;
